import React from "react";

import { Box, Typography } from "@mui/material";

import { AssistantMutedText } from "../../styles";
import { assistantScenarioLegend } from "./constants";
import {
  AssistantFeatureWalkthroughSection,
  AssistantScenarioButton,
  AssistantScenarioLegend,
  AssistantScenarioLegendDot,
  AssistantScenarioLegendItem,
  AssistantScenarioList,
  AssistantScenarioProgress,
  AssistantScenarioSegment,
  AssistantScenarioStatus,
  AssistantScenarioSymbol,
  AssistantScenarioTitle,
} from "./styles";
import { AssistantFeatureSummary } from "./summary";
import { IAssistantFeatureWalkthroughProps } from "./types";
import { getAssistantScenarioChecklist } from "./utils";

const AssistantFeatureWalkthrough: React.FC<IAssistantFeatureWalkthroughProps> =
  ({ feature, pending, finished, onSelect, onResume }) => {
    const { scenarios, completed, skipped, unfinished } =
      getAssistantScenarioChecklist(feature);

    if (finished)
      return (
        <AssistantFeatureSummary
          scenarios={scenarios}
          counts={{ completed, skipped, unfinished }}
          onResume={onResume}
        />
      );

    return (
      <AssistantFeatureWalkthroughSection aria-label="Feature walkthrough">
        <AssistantMutedText variant="subtitle9">
          Feature walkthrough
        </AssistantMutedText>
        <Typography variant="h5" component="h3">
          {feature.title}
        </Typography>
        <AssistantMutedText variant="subtitle7">
          {feature.description}
        </AssistantMutedText>
        <Typography variant="subtitle9" role="status">
          {completed} of {scenarios.length} completed · {skipped} skipped
        </Typography>
        <AssistantScenarioProgress aria-label="Scenario progress">
          {scenarios.map((scenario) => (
            <AssistantScenarioSegment
              key={scenario.id}
              status={scenario.status}
              title={`${scenario.title}: ${scenario.label}`}
            />
          ))}
        </AssistantScenarioProgress>
        <AssistantScenarioLegend aria-label="Scenario status legend">
          {assistantScenarioLegend.map((item) => (
            <AssistantScenarioLegendItem key={item.status} variant="subtitle9">
              <AssistantScenarioLegendDot status={item.status} />
              {item.label}
            </AssistantScenarioLegendItem>
          ))}
        </AssistantScenarioLegend>
        <AssistantScenarioList>
          {scenarios.map((scenario) => (
            <AssistantScenarioButton
              key={scenario.id}
              variant="text"
              status={scenario.status}
              disabled={pending}
              onClick={() => onSelect(scenario.id)}
              aria-label={`${scenario.title}: ${scenario.label}`}
            >
              <AssistantScenarioSymbol status={scenario.status} aria-hidden>
                {scenario.symbol}
              </AssistantScenarioSymbol>
              <AssistantScenarioTitle>{scenario.title}</AssistantScenarioTitle>
              <AssistantScenarioStatus status={scenario.status}>
                {scenario.label}
              </AssistantScenarioStatus>
              <Box component="span" aria-hidden>
                ›
              </Box>
            </AssistantScenarioButton>
          ))}
        </AssistantScenarioList>
      </AssistantFeatureWalkthroughSection>
    );
  };

export default AssistantFeatureWalkthrough;
